import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';

import UserNotification from 'util/UserNotification';
import type { ColumnFilterData } from 'security-app/components/common/Filters/ColumnFilter.types';
import type { EventDefinition } from 'components/event-definitions/event-definitions-types';

import {
  fetchSecurityEvent,
  fetchSecurityEvents,
  newSecurityEvent,
  updateSecurityEvent,
  fetchUsersNTeams,
  triggerSecurityEventNotification,
  fetchEventDefinitions,
  fetchEventDefinitionDetails,
  fetchEntityTypes,
  toggleEventDefinitionStatus,
  copyEventDefinition,
  clearEventDefinitionNotificationsQueue,
  deleteEventDefinition,
  blkEnableDefinition,
  blkDisableDefinition,
  blkDeleteDefinition,
  fetchNotifications,
  fetchNotificationDetails,
  deleteNotification,
  testNotification,
} from './api/securityEventsAPI';
import type {
  OwnerOptionType,
  PaginatedEventDefinitionsAPIType,
  PaginatedSecurityEventsAPIType,
  PaginatedNotificationsAPIType,
  SecurityEventAPIType,
} from './api/securityEventsAPI.types';

export type PaginatedProps = {
  page: number,
  perPage: number,
  query?: string,
  orderBy?: string,
  direction?: 'asc' | 'desc',
  filters?: ColumnFilterData,
};

export function useGetSecurityEvent(eventId: string) {
  const { data, isLoading } = useQuery<SecurityEventAPIType, Error>(
    ['get-security-events', { id: eventId }],
    () => fetchSecurityEvent(eventId),
    {
      onError: (error: Error) => UserNotification.error(error.message),
      retry: 2,
    },
  );

  return {
    loadingSecurityEvents: isLoading,
    securityEvent: data,
  };
}

export function useGetSecurityEvents({
  page,
  perPage,
  query,
  orderBy,
  direction,
  filters,
}: PaginatedProps, userTimezone?: string) {
  const { data, isLoading } = useQuery<PaginatedSecurityEventsAPIType, Error>(
    ['get-security-events', page, perPage, query, orderBy, direction, filters, userTimezone],
    () => fetchSecurityEvents(page, perPage, query, orderBy, direction, filters, userTimezone),
    {
      onError: (error: Error) => UserNotification.error(error.message),
      retry: 2,
      keepPreviousData: true,
    },
  );

  return {
    loadingSecurityEvents: isLoading,
    securityEvents: isLoading ? [] : data?.security_events || [],
    pagination: {
      page: data?.page || page,
      perPage: data?.per_page || perPage,
      total: data?.total || 0,
      grandTotal: data?.grand_total || 0,
      count: data?.count || 0,
    },
  };
}

export function useCreateSecurityEvent() {
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading } = useMutation(
    newSecurityEvent,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['get-security-events']);
      },
      onError: (error: Error) => UserNotification.error(error.message),
    },
  );

  return {
    createSecurityEvent: mutateAsync,
    creatingSecurityEvent: isLoading,
  };
}

export function useUpdateSecurityEvent() {
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading } = useMutation(
    updateSecurityEvent,
    {
      onSuccess: () => {
        UserNotification.success('Event updated successfully');
        queryClient.invalidateQueries(['get-security-events']);
      },
      onError: (error: Error) => UserNotification.error(error.message),
    },
  );

  return {
    updateSecurityEvent: mutateAsync,
    updatingSecurityEvent: isLoading,
  };
}

export function useGetUsersNTeams() {
  const { data, isLoading } = useQuery<OwnerOptionType[], Error>(
    ['get-users-n-teams'],
    fetchUsersNTeams,
    {
      onError: (error: Error) => UserNotification.error(error.message),
      retry: 2,
    },
  );

  return {
    loadingUsersNTeams: isLoading,
    usersNTeams: data || [],
  };
}

export function useTriggerSecurityEventNotification() {
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading } = useMutation(
    triggerSecurityEventNotification,
    {
      onSuccess: () => {
        UserNotification.success('Notification triggered successfully');
        queryClient.invalidateQueries(['trigger-security-event-notification']);
      },
      onError: (error: Error) => UserNotification.error(error.message),
    },
  );

  return {
    triggerSecurityEventNotification: mutateAsync,
    triggeringSecurityEventNotification: isLoading,
  };
}

/**
  * Event Definitions
* */

export function useGetEventDefinitions({
  page,
  perPage,
  query,
  orderBy,
  direction,
}: PaginatedProps) {
  const { data, isLoading } = useQuery<PaginatedEventDefinitionsAPIType, Error>(
    ['get-event-definitions', page, perPage, query, orderBy, direction],
    () => fetchEventDefinitions(page, perPage, query, orderBy, direction),
    {
      onError: (error: Error) => UserNotification.error(error.message),
      retry: 2,
      keepPreviousData: true,
    },
  );

  return {
    loadingEventDefinitions: isLoading,
    eventDefinitions: isLoading ? [] : data?.elements || [],
    pagination: data?.pagination || {
      total: 0,
      count: 0,
      page: 1,
      per_page: 10,
    },
  };
}

export function useGetEventDefinitionDetails(eventDefinitionIds: string[]) {
  const results = useQueries<EventDefinition[]>({
    queries: eventDefinitionIds.map((eventDefId: string) => ({
      queryKey: ['get-event-definition-details', eventDefId],
      queryFn: () => fetchEventDefinitionDetails(eventDefId),
      onError: (error: Error) => {
        if (!error.message.match(/not found/i)) UserNotification.error(error.message);
      },
      enabled: eventDefinitionIds.length > 0,
    })),
  });

  const { data, isLoading } = results.reduce((acc, result) => {
    if (Array.isArray(acc.data)) acc.data.push(result.data);
    else acc.data = [result.data];
    acc.isLoading = results.some((res) => res.isLoading);

    return acc;
  }, { data: [], isLoading: false });

  return {
    loadingEventDefinition: isLoading,
    eventDefinitions: data,
  };
}

export function useGetEntityTypes() {
  const { data, isLoading } = useQuery<{ processor_types: string[]}, Error>(
    ['get-entity-types'],
    fetchEntityTypes,
    {
      onError: (error: Error) => UserNotification.error(error.message),
      retry: 2,
      keepPreviousData: true,
    },
  );

  return {
    loadingEntityTypes: isLoading,
    entityTypes: isLoading ? [] : data.processor_types.reduce((acc, procType) => {
      if (procType === 'aggregation-v1') acc[procType] = 'Filter & Aggregation';
      if (procType === 'correlation-v1') acc[procType] = 'Event Correlation';
      if (procType === 'system-notifications-v1') acc[procType] = 'System Notifications';
      if (procType === 'anomaly-v1') acc[procType] = 'Anomaly Detector';
      if (procType === 'sigma-v1') acc[procType] = 'Sigma Detector';

      return acc;
    }, {}),
  };
}

export function useToggleEventDefinitionStatus() {
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading } = useMutation(
    toggleEventDefinitionStatus,
    {
      onSuccess: () => {
        UserNotification.success('Event definition status updated successfully');
        queryClient.invalidateQueries(['get-event-definitions']);
      },
      onError: (error: Error) => UserNotification.error(error.message),
    },
  );

  return {
    toggleEventDefinitionStatus: mutateAsync,
    togglingEventDefinitionStatus: isLoading,
  };
}

export function useCopyEventDefinition() {
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading } = useMutation(
    copyEventDefinition,
    {
      onSuccess: () => {
        UserNotification.success('Event definition copied successfully');
        queryClient.invalidateQueries(['get-event-definitions']);
      },
      onError: (error: Error) => UserNotification.error(error.message),
    },
  );

  return {
    copyEventDefinition: mutateAsync,
    copyingEventDefinition: isLoading,
  };
}

export function useClearEventDefinitionNotificationsQueue() {
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading } = useMutation(
    clearEventDefinitionNotificationsQueue,
    {
      onSuccess: () => {
        UserNotification.success('Event definition notifications queue cleared successfully');
        queryClient.invalidateQueries(['get-event-definitions']);
      },
      onError: (error: Error) => UserNotification.error(error.message),
    },
  );

  return {
    clearEventDefinitionNotificationsQueue: mutateAsync,
    clearingEventDefinitionNotificationsQueue: isLoading,
  };
}

export function useDeleteEventDefinition() {
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading } = useMutation(
    deleteEventDefinition,
    {
      onSuccess: () => {
        UserNotification.success('Event definition deleted successfully');
        queryClient.invalidateQueries(['get-event-definitions']);
        queryClient.invalidateQueries(['get-security-events']);
      },
      onError: (error: Error) => UserNotification.error(error.message),
    },
  );

  return {
    deleteEventDefinition: mutateAsync,
    deletingEventDefinition: isLoading,
  };
}

export function useBlkEnableDefinition() {
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading } = useMutation(
    blkEnableDefinition,
    {
      onSuccess: () => {
        UserNotification.success('Event definitions enabled successfully');
        queryClient.invalidateQueries(['get-event-definitions']);
      },
      onError: (error: Error) => UserNotification.error(error.message),
    },
  );

  return {
    blkEnableDefinition: mutateAsync,
    blkEnablingDefinition: isLoading,
  };
}

export function useBlkDisableDefinition() {
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading } = useMutation(
    blkDisableDefinition,
    {
      onSuccess: () => {
        UserNotification.success('Event definitions disabled successfully');
        queryClient.invalidateQueries(['get-event-definitions']);
      },
      onError: (error: Error) => UserNotification.error(error.message),
    },
  );

  return {
    blkDisableDefinition: mutateAsync,
    blkDisablingDefinition: isLoading,
  };
}

export function useBlkDeleteDefinition() {
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading } = useMutation(
    blkDeleteDefinition,
    {
      onSuccess: () => {
        UserNotification.success('Event definitions deleted successfully');
        queryClient.invalidateQueries(['get-event-definitions']);
      },
      onError: (error: Error) => UserNotification.error(error.message),
    },
  );

  return {
    blkDeleteDefinition: mutateAsync,
    blkDeletingDefinition: isLoading,
  };
}

/**
  * Notifications
* */

export function useGetNotifications({ page, perPage, query, orderBy, direction }: PaginatedProps) {
  const { data, isLoading } = useQuery<PaginatedNotificationsAPIType, Error>(
    ['get-security-event-notifications', page, perPage, query, orderBy, direction],
    () => fetchNotifications(page, perPage, query, orderBy, direction),
    {
      onError: (error: Error) => UserNotification.error(error.message),
      retry: 2,
      keepPreviousData: true,
    },
  );

  return {
    loadingNotifications: isLoading,
    notifications: isLoading ? [] : data?.elements || [],
    pagination: {
      page: data?.pagination.page || page,
      perPage: data?.pagination.per_page || perPage,
      total: data?.pagination.total || 0,
      grandTotal: data?.pagination.total || 0,
      count: data?.pagination.count || 0,
    },
  };
}

export function useGetNotificationsDetails(notificationIds: string[]) {
  const results = useQueries({
    queries: notificationIds.map((notificationId: string) => ({
      queryKey: ['get-security-event-notification-details', notificationId],
      queryFn: () => fetchNotificationDetails(notificationId),
      onError: (error: Error) => UserNotification.error(error.message),
      enabled: notificationIds.length > 0,
    })),
  });

  const { data, isLoading } = results.reduce((acc, result) => {
    if (Array.isArray(acc.data)) acc.data.push(result.data);
    else acc.data = [result.data];
    acc.isLoading = results.some((res) => res.isLoading);

    return acc;
  }, { data: [], isLoading: false });

  return {
    loadingNotifications: isLoading,
    notifications: data,
  };
}

export function useDeleteNotification() {
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading } = useMutation(
    deleteNotification,
    {
      onSuccess: () => {
        UserNotification.success('Notification deleted successfully');
        queryClient.invalidateQueries(['get-security-event-notifications']);
      },
      onError: (error: Error) => UserNotification.error(error.message),
    },
  );

  return {
    deleteNotification: mutateAsync,
    deletingNotification: isLoading,
  };
}

export function useTestNotification() {
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading } = useMutation(
    testNotification,
    {
      onSuccess: () => {
        UserNotification.success('Notification sent successfully');
        queryClient.invalidateQueries(['get-security-event-notifications']);
      },
      onError: (error: Error) => UserNotification.error(error.message),
    },
  );

  return {
    testNotification: mutateAsync,
    testingNotification: isLoading,
  };
}
